import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import useProjectContext from "@/services/context/project/use-project-context";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";
import ENDPOINTS from "@/services/endpoints";
import MEASURE_ENDPOINTS from "@/services/endpoints/measure-endpoints.ts";

import { fetchItem, putItem } from "@/queries/queries-helper";
import UpdateStoreyMeasureDto from "@/models/interfaces/dtos/measure/update/update-storey-measure-dto";
import UpdateGeometryRepresentationDto from "@/models/interfaces/dtos/measure/update/update-geometry-representation-dto";
import MeasureDto from "@/models/interfaces/dtos/measure/get/measure-dto";
import PathElementDto from "@/models/interfaces/dtos/path-element-dto";
import FragmentRecord from "@/pages/takeoff/context/project-measure/models/fragment-record";
import Measure from "@/pages/takeoff/context/project-measure/models/measure";
import GeometryRepresentation from "@/pages/takeoff/context/project-measure/models/geometry-representation";
import PlanFileData from "@/pages/takeoff/context/project-measure/models/plan-file-data";
import { MeasureSelection, StoreyMeasuresContext, StoreyMeasuresContextProps } from "./storey-measures-context-props";

export interface StoreyMeasuresContextProviderProps extends React.PropsWithChildren {
    storeyId: string;
}

const StoreyMeasuresProviders = ({ storeyId, children }: StoreyMeasuresContextProviderProps) => {
    const { project } = useProjectContext();

    const { projectMeasures, storeysFileData, storeysFragsData, baamerFileRevisions, tempMeasureTypes, deletedMeasureTypeIds, updateMeasuresAndRecords, invalidateMeasureTypes } = useProjectMeasureContext();

    const { building, storey } = useMemo(() =>
        projectMeasures.getBuildingAndStoreyFromStoreyId(storeyId) ?? { building: undefined, storey: undefined }, [projectMeasures, storeyId]);

    const buildingId = building?.id;

    const storeyFilesRecord = useMemo(() => storeysFileData.get(storeyId), [storeyId, storeysFileData]);
    const storeyFragsRecord = useMemo(
        () => storeysFragsData.get(storeyId) ?? [],
        [storeyId, storeysFragsData]
    );

    const [measures, setMeasures] = useState<Measure[]>([]);
    const measuresRef = useRef<Measure[]>([]);

    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [selectedMeasures, setSelectedMeasures] = useState<MeasureSelection | undefined>(undefined);

    const pendingSelectionClearTimeout = useRef<ReturnType<typeof window.setTimeout> | undefined>(undefined);
    const ignoreSelectionEventsUntil = useRef(0);

    const handleGeometriesSelected = useCallback((geometries: string[]) => {
        if (Date.now() < ignoreSelectionEventsUntil.current) {
            return;
        }

        if (pendingSelectionClearTimeout.current != null) {
            window.clearTimeout(pendingSelectionClearTimeout.current);
            pendingSelectionClearTimeout.current = undefined;
        }

        if (geometries.length === 0) {
            pendingSelectionClearTimeout.current = window.setTimeout(() => {
                setSelectedMeasures(undefined);
                pendingSelectionClearTimeout.current = undefined;
            }, 50);

            return;
        }

        const currentMeasures = measuresRef.current;
        const foundMeasures = currentMeasures.filter(measure =>
            geometries.some(geometryId =>
                measure.geometryRepresentations.some(geometry => geometry.geometryId === geometryId)
            )
        );

        if (foundMeasures.length === 0) return;

        const measureIdsKey = foundMeasures.map(m => m.measureId).sort().join(",");

        setSelectedMeasures(previous => {
            if (previous != null && previous.origin === "viewer") {
                const prevIdsKey = previous.measures.map(m => m.measureId).sort().join(",");
                if (prevIdsKey === measureIdsKey) {
                    return previous;
                }
            }

            return {
                measures: foundMeasures,
                origin: "viewer"
            };
        });
    }, [setSelectedMeasures]);

    useEffect(
        () => () => {
            if (pendingSelectionClearTimeout.current != null) {
                window.clearTimeout(pendingSelectionClearTimeout.current);
            }
        },
        []
    );

    const [tempMeasures, setTempMeasures] = useState<Measure[]>([]);

    useEffect(() => {
        measuresRef.current = measures;
    }, [measures]);

    useEffect(() => {
        if (!isDirty) {
            const clonedMeasures = storey?.measures.map(m => m.clone()) ?? [];
            measuresRef.current = clonedMeasures;
            setMeasures(clonedMeasures);
        }
    }, [isDirty, storey]);

    const addMeasure = useCallback((measure: Measure) => {
        setMeasures(prev => {
            const updatedMeasures = [...prev, measure];
            measuresRef.current = updatedMeasures;
            return updatedMeasures;
        });
        setTempMeasures(prev => [...prev, measure]);
        setIsDirty(true);
    }, []);

    const editMeasure = useCallback((measureId: string, measure: Measure) => {
        setMeasures(prev => {
            const updatedMeasures = prev.map(m => m.measureId === measureId ? measure : m);
            measuresRef.current = updatedMeasures;
            return updatedMeasures;
        });
        setTempMeasures(prev => prev.map(m => m.measureId === measureId ? measure : m));
        setIsDirty(true);
    }, []);

    const removeMeasure = useCallback((measureId: string) => {
        setSelectedMeasures(previous => {
            if (previous == null) return undefined;

            const remainingMeasures = previous.measures.filter(measure => measure.measureId !== measureId);

            if (remainingMeasures.length === 0) return undefined;

            return {
                ...previous,
                measures: remainingMeasures
            };
        });

        setMeasures(prev => {
            const updatedMeasures = prev.filter(m => m.measureId !== measureId);
            measuresRef.current = updatedMeasures;
            return updatedMeasures;
        });
        setTempMeasures(prev => prev.filter(m => m.measureId !== measureId));
        setIsDirty(true);
    }, []);

    const saveMeasures = useCallback(async (measuresBuffer: ArrayBuffer) => {
        setIsSaving(true);

        const measuresToSave = measuresRef.current;

        const updateMeasureDtos: UpdateStoreyMeasureDto[] = measuresToSave.map(measure => ({
            measureId: measure.measureId,
            name: measure.name,
            measureTypeId: measure.measureTypeId,
            dataInformations: JSON.stringify(measure.dataInformations),
            geometryRepresentations: measure.geometryRepresentations.map<UpdateGeometryRepresentationDto>(geometry => ({
                geometryRepresentationId: geometry.geometryRepresentationId,
                geometryId: geometry.geometryId,
                operation: geometry.operation
            }))
        }));

        const formData = new FormData();
        formData.append(
            "measures",
            JSON.stringify(updateMeasureDtos)
        );
        formData.append(
            "measuresBuffer",
            new Blob([measuresBuffer], { type: "application/octet-stream" }),
            "Measures.frag"
        );

        try {
            const result = await putItem<MeasureDto[]>(MEASURE_ENDPOINTS.UPDATE_STOREY_MEASURES(storeyId), formData);

            if (result) {
                const updatedMeasures: Array<Measure> = [];

                result.forEach(measureDto => {
                    let dataInformations: Record<string, unknown>;
                    if (measureDto.dataInformations) {
                        try {
                            dataInformations = JSON.parse(measureDto.dataInformations);
                        } catch {
                            dataInformations = {};
                        }
                    } else {
                        dataInformations = {};
                    }

                    const measure = new Measure(measureDto.measureType.measureTypeId, measureDto.name, measureDto.measureId, dataInformations);

                    measureDto.geometryRepresentations.forEach(representationDto => {
                        const geometry = new GeometryRepresentation(representationDto.geometryId, representationDto.operation, representationDto.geometryRepresentationId);
                        measure.geometryRepresentations.push(geometry);
                    })

                    updatedMeasures.push(measure);
                })

                setMeasures(updatedMeasures);
                measuresRef.current = updatedMeasures;

                const currentBfr = baamerFileRevisions?.find(bfr =>
                    bfr.storeyFileRevisions.some(sfr => sfr.storeyId === storeyId)
                );

                const newFragsData: FragmentRecord[] = [];
                if (currentBfr) {
                    const storeyFragments = await fetchItem<PathElementDto[]>(
                        ENDPOINTS.PROJECT_MEASURE_3D_FILES(project.id, currentBfr.baamerRevisionId, buildingId, storeyId)
                    ) ?? [];
                    storeyFragments.forEach(fragData => {
                        let parsedData: Record<string, unknown> = {};
                        if (currentBfr.data) {
                            try {
                                parsedData = JSON.parse(currentBfr.data) as Record<string, unknown>;
                            } catch {
                                // Intentionally ignore invalid JSON
                            }
                        }

                        const planFileData = PlanFileData.fromJSON(parsedData);

                        const fileFrag = new FragmentRecord(fragData.name, fragData.path, planFileData.origin, planFileData.sitePosition);

                        newFragsData.push(fileFrag);
                    });
                }

                if (buildingId && storeyFilesRecord) {
                    updateMeasuresAndRecords(buildingId, storeyId, updatedMeasures, storeyFilesRecord, newFragsData);
                }

                if (tempMeasureTypes.length > 0 || deletedMeasureTypeIds.length > 0)
                    invalidateMeasureTypes();

                setTempMeasures([]);
                setIsDirty(false);
                return true;
            }
        } catch (error) {
            if (error instanceof Error)
                toast.error(error.message, { autoClose: false });
            // console.error(e);
        } finally {
            setIsSaving(false);
        }
        return false;

    }, [storeyId, baamerFileRevisions, buildingId, storeyFilesRecord, tempMeasureTypes.length, deletedMeasureTypeIds.length, invalidateMeasureTypes, project.id, updateMeasuresAndRecords]);

    const updateProjectData = useCallback(() => {
        if (storeyFilesRecord == null || buildingId == null) return;
        updateMeasuresAndRecords(buildingId, storeyId, measures, storeyFilesRecord, storeyFragsRecord);
    }, [buildingId, measures, storeyFilesRecord, storeyFragsRecord, storeyId, updateMeasuresAndRecords]);

    if (building == null || storey == null || measures == null || storeyFragsRecord == null || storeyFilesRecord == null) return (
        <>Une erreur est survenue</>
    )

    const contextProps: StoreyMeasuresContextProps = {
        building,
        storey,
        measures,
        measuresRef,
        tempMeasures,

        selectedMeasures,
        setSelectedMeasures,
        handleGeometriesSelected,

        isSaving,
        isDirty,

        storeyFilesRecord,
        storeyFragsRecord,

        addMeasure,
        editMeasure,
        removeMeasure,
        saveMeasures,

        updateProjectData
    }

    return (
        <StoreyMeasuresContext.Provider value={contextProps}>
            {children}
        </StoreyMeasuresContext.Provider>
    );
}

export default StoreyMeasuresProviders;